import _ from "lodash";
import { IElementVariable, populateAccessibleReturnsAndVariables } from "processEditor/actions/VariableScopeAnalyzer";
import { PromptType } from "processEditor/components/Modals/AssistantModal";
import { ExpressionParameters } from "processEditor/components/ExpressionEditor";
import { ElementType, IElementDictionary, IParameterSchema, IParameters } from "processEditor/model/IProcessData";
import { getTriggerReferences } from "processEditor/model/References";
import { State } from "processEditor/model/State";
import { useState, useEffect } from "react";
import { useSelector } from 'react-redux'

export interface IUseAzureAiContextGather {
    variables?: string[];
    taskResults?: { description: string | undefined, type: string | undefined, name: string }[];
    otherResults?: string[];
    environmentVariables?: string[];
    triggers?: string[];
    targetFramework?: string;
    assignVariable?: boolean;
    taskParameterMetadata?: IFilteredParameterSchema[];
    taskParameterJson?: IParameters;
}

export interface ITaskCompletionData {
    taskId: string;
    parameterSchema: IParameterSchema[];
    parameters: IParameters;

}

export const useTaskCompletionDataGather = (elementId: string) => {
    const [context, setContext] = useState<ITaskCompletionData | null>(null);
    const state = useSelector<State, State>(state => state);
    useEffect(() => {
        const element = state.elements[elementId];
        if (element == null) {
            return;
        }
        const taskId = element.selectedTypeId;
        if (element.type != ElementType.Task || element.selectedTypeId == null) {
            return;
        }
        const task = state.tasks.find(t => t.uri === taskId);
        const schema = JSON.parse(task?.argumentsJson as string) as IParameterSchema[];
        const context: ITaskCompletionData = {
            parameters: element.parameters,
            parameterSchema: schema,
            taskId: taskId!
        };
        setContext(context);
    }, [elementId]);
    return context;
}

const useAzureAiContextGather = (elementId: string, promptType: PromptType): IUseAzureAiContextGather => {

    const [context, setContext] = useState<IUseAzureAiContextGather>({ environmentVariables: [], variables: [], otherResults: [], taskResults: [], triggers: [], targetFramework: "", assignVariable: true });
    const state = useSelector<State, State>(state => state);
    const taskData = useTaskCompletionDataGather(elementId);

    useEffect(() => {
        const accessibleVariablesWithinScope: IElementVariable[] = [];
        const accessibleReturnsWithinScope: IElementVariable[] = [];;

        populateAccessibleReturnsAndVariables(
            elementId,
            state.validation.variableScopeDictionary,
            accessibleReturnsWithinScope,
            accessibleVariablesWithinScope);


        const elements = _.map(state.elements, (e) => e);
        const manualTriggerParameters = state.process.manualTrigger.map((x) => x.name);
        const triggerReferences = getTriggerReferences(elements.filter((x) => x.type === ElementType.Start), manualTriggerParameters, state.process.isForMonitoringRule)

        // Get task uri and variable name
        const taskIdAndVariableName = accessibleReturnsWithinScope
            .filter((x) => x.elementType === ElementType.Task)
            .map((x) => { return { taskUri: elements.find((e) => e.id === x.elementId)?.selectedTypeId, variableName: x.variableName } });

        // Get task information from state
        const taskResults = taskIdAndVariableName
            .map((x) => { return { task: state.tasks.find((t) => t.uri === x.taskUri), variableName: x.variableName } })
            .map((x) => { return { description: x.task?.description, type: x.task?.packageName, name: `#result[${x.variableName}]` } });


        // Gather context to be sent to AI-Completion. 
        // Environment variables are currently all variables for whole tenant, could this grow too large and be filtered to only those used in process.
        const context: IUseAzureAiContextGather = {
            variables: accessibleVariablesWithinScope.map(v => `#var.${v.variableName}`).concat(state.processVariables.map((v) => `#var.${v.name}`)),
            taskResults: taskResults,
            otherResults: accessibleReturnsWithinScope.filter((x) => x.elementType !== ElementType.Task).map(v => `#result[${v.variableName}]`),
            environmentVariables: state.environmentVariables
                .filter(v => v.includes(".")) // Don't send groups / root keys
                .slice(0, 50) // take the first 50 values automatically
                .map(v => `#env.${v}`),
            triggers: triggerReferences.map(v => `#trigger.${v}`),
            targetFramework: state.process.targetFramework,
        }

        const element = state.elements[elementId];

        // Gather additional context based on prompt type
        if (promptType === PromptType.CodeCompletion && element.type === ElementType.Expression) {
            const shouldAssignVariable = (element.parameters as ExpressionParameters).shouldAssignVariable;
            context.assignVariable = shouldAssignVariable.value;
        }

        if (promptType === PromptType.TaskCompletion && element.type === ElementType.Task) {

            if (!taskData) {
                console.log(`Task data for element ${elementId} is not available yet`);
                return;
            }
            context.taskParameterJson = taskData.parameters;
            const filteredSchema: IFilteredParameterSchema[] = taskData.parameterSchema.map(filterSchema);
            context.taskParameterMetadata = removeNullValues(filteredSchema) as IFilteredParameterSchema[];
        }

        setContext(context);
    }, [elementId, promptType, taskData]);

    return context;
};

export function removeNullValues(obj: IFilteredParameterSchema[] | IFilteredParameterSchema): IFilteredParameterSchema[] | IFilteredParameterSchema {
    if (Array.isArray(obj)) {
        // Process each item in the array
        return obj
            .map(item => removeNullValues(item)) // Recursively clean nested items
            .filter((item): item is IFilteredParameterSchema => item !== null); // Filter o
    } else if (typeof obj === 'object' && obj !== null) {
        // Process each key in the object
        const result: IFilteredParameterSchema = {
            ...obj
        };
        for (const key of Object.keys(obj)) {
            const value = obj[key];
            if (value !== null) {
                // Recursively clean nested objects/arrays
                result[key] = removeNullValues(value);
            } else {
                delete result[key];
            }

        }
        return result;
    }
    return obj; // Return primitive values as is
}

function filterSchema(param: IParameterSchema): IFilteredParameterSchema {
    return {
        name: param.name,
        type: param.type,
        description: param.description,
        parameterType: param.parameterType,
        parameterTemplate: param.parameterTemplate ? filterSchema(param.parameterTemplate) : null,
        subParameters: param.subParameters ? param.subParameters.map(filterSchema) : null,
    };
}

interface IFilteredParameterSchema {
    name: string;
    type: string;
    description?: string;
    parameterType: string;
    parameterTemplate?: IFilteredParameterSchema | null;
    subParameters?: IFilteredParameterSchema[] | null;
    [key: string]: any; // Allows other properties
}

export default useAzureAiContextGather;